import {
    LatLng,
    MapOptions,
    MarkerOptions,
    PolylineOptions,
    PolylineStyleOptions,
    PolygonOptions,
    MapBounds,
    ClusterOptions,
    ClusteringType,
    MapEventType,
    MapEvent,
    IMapProvider
} from 'app/shared/map/interfaces';
import {decodePolyline, decodeMultiplePolylines} from 'app/shared/map/utils/polyline-decoder.util';
import {environment} from 'environments/environment';
import {registerScreenshotMap} from 'app/blocks/service/assistant-context/assistant-map-snapshot';

declare let H: any;

/**
 * HERE Maps Provider
 * Handles all HERE Maps-specific operations
 */
export class HereMapProvider implements IMapProvider {
    private map: any = null; // H.Map
    private platform: any = null; // H.service.Platform
    private ui: any = null; // H.ui.UI
    private behavior: any = null; // H.mapevents.Behavior
    private markers: Map<string, any> = new Map(); // Map of marker IDs to H.map.Marker
    private polylines: Map<string, any> = new Map(); // Map of polyline IDs to H.map.Polyline
    private polygons: Map<string, any> = new Map(); // Map of polygon IDs to H.map.Polygon
    private groups: Map<string, any> = new Map(); // Map of group IDs to H.map.Group
    private markerGroup: any = null; // H.map.Group for all markers
    private clusterProvider: any = null; // H.clustering.Provider
    private clusterLayer: any = null; // Clustering layer
    private drawnItems: any = null; // For polygon drawing
    private eventHandlers: Map<string, ((e: MapEvent) => void)[]> = new Map();
    private idCounter = 0;
    private openBubbles: Map<string, any> = new Map(); // Map of bubble IDs to H.ui.InfoBubble
    private hoverPopupCooldownUntil = 0; // timestamp: suppress hover-triggered popups after programmatic showPopup
    // Distinguishes genuine user hover (mouse moved) from HERE Maps' synthetic pointerenter
    // events that fire when a new cluster layer is rendered under a stationary cursor.
    // Reset to false on rebuildClusterLayer(); set back to true on any pointermove.
    private mouseHasMovedSinceRebuild = true;
    /** removes this map from the assistant screenshot registry (CU-14zefhy0dgu MAP-HM) */
    private unregisterScreenshot: () => void = () => undefined;

    // Clustering options
    private clusteringType: ClusteringType = ClusteringType.COUNT;
    private valueExtractor: ((markerData: any) => number) | null = null;
    private customIconCreateFunction: ((cluster: any) => any) | null = null;
    private clusteringEnabled: boolean = false;
    private storedClusterOptions: ClusterOptions | null = null;

    // Polygon drawing
    private polygonDrawingEnabled: boolean = false;
    private polygonDrawingId: string = null;
    private onPolygonComplete: ((coordinates: LatLng[], polygonId?: string) => void) | null = null;
    private onPolygonEdit: ((coordinates: LatLng[]) => void) | null = null;
    private polygonFirstLat: number;
    private polygonFirstLng: number;

    // Point movement
    private movePointEnabled: boolean = false;
    private movePointId: string = null;

    private readonly apiKey = environment.heremapsTourPlanningCredentials.apiKey;

    /**
     * Generate unique ID for map objects
     */
    private generateId(): string {
        return `map-obj-${++this.idCounter}`;
    }

    /**
     * Initialize the map
     */
    initialize(element: HTMLElement, options: MapOptions): void {
        if (this.map) {
            this.destroy();
        }

        // Initialize HERE Maps Platform
        this.platform = new H.service.Platform({
            apikey: this.apiKey,
            useHTTPS: true
        });

        // Get pixel ratio for better rendering
        const pixelRatio = window.devicePixelRatio || 1;
        const engineType = H.Map.EngineType['HARP'];

        // Create default layers
        const defaultLayers = this.platform.createDefaultLayers({
            engineType,
            tileSize: pixelRatio === 1 ? 256 : 512,
            ppi: pixelRatio === 1 ? undefined : 320
        });

        // Create the map
        this.map = new H.Map(element, defaultLayers.vector.normal.map, {
            engineType,
            pixelRatio: pixelRatio,
            center: {lat: options.center.lat, lng: options.center.lng},
            zoom: options.zoom,
            padding: {top: 50, left: 50, bottom: 50, right: 50}
        });
        // CU-14zefhy0dgu MAP-HM: lets an assistant screenshot draw this WebGL map (captured only at screenshot time)
        this.unregisterScreenshot = registerScreenshotMap(this.map, element);

        // Enable map events (pan, zoom, etc.)
        const mapEvents = new H.mapevents.MapEvents(this.map);
        this.behavior = new H.mapevents.Behavior(mapEvents);

        // Create UI controls
        this.ui = H.ui.UI.createDefault(this.map, defaultLayers);

        // Configure controls
        if (options.zoomControl !== false) {
            this.ui.getControl('zoom').setDisabled(false);
            this.ui.getControl('zoom').setAlignment('top-left');
        } else {
            this.ui.getControl('zoom').setDisabled(true);
        }

        if (options.layerControl !== false) {
            this.ui.getControl('mapsettings').setDisabled(false);
            this.ui.getControl('mapsettings').setAlignment('top-left');
        } else {
            this.ui.getControl('mapsettings').setDisabled(true);
        }

        if (options.scaleControl !== false) {
            this.ui.getControl('scalebar').setDisabled(false);
            this.ui.getControl('scalebar').setAlignment('top-left');
        } else {
            this.ui.getControl('scalebar').setDisabled(true);
        }

        // Initialize marker group
        this.markerGroup = new H.map.Group();
        this.map.addObject(this.markerGroup);

        // Close all popups when clicking on the map (outside of markers/bubbles)
        this.map.addEventListener('tap', (evt: any) => {
            if (this.polygonDrawingEnabled) {
                this.handleTapEvent(evt);
            } else {
                // Close all popups when clicking anywhere on the map
                // Note: Markers with popups will stop propagation, so this won't trigger for them
                this.closeAllPopups();
            }
        });

        this.map.addEventListener(
            'dragstart',
            (evt: any) => {
                const target = evt.target;
                if (this.polygonDrawingEnabled) {
                    if (target.data && target.data.type === 'vertex-marker') {
                        this.behavior.disable();
                    }
                }
                if (this.movePointEnabled) {
                    if (target.data && target.data.type === 'movable-point') {
                        this.behavior.disable();
                    }
                }
            },
            false
        );

        this.map.addEventListener(
            'dragend',
            (evt: any) => {
                const target = evt.target;
                if (this.polygonDrawingEnabled) {
                    if (target.data && target.data.type === 'vertex-marker') {
                        this.behavior.enable();
                    }
                }
                if (this.movePointEnabled) {
                    if (target.data && target.data.type === 'movable-point') {
                        this.behavior.enable();
                    }
                }
            },
            false
        );

        // Track real mouse movement so we can suppress HERE Maps' synthetic pointerenter events
        // that fire when a new cluster layer is rendered under a stationary cursor.
        this.map.addEventListener('pointermove', () => {
            this.mouseHasMovedSinceRebuild = true;
        });
    }

    /**
     * Destroy the map and cleanup
     */
    destroy(): void {
        this.unregisterScreenshot();
        this.unregisterScreenshot = () => undefined;
        if (this.map) {
            this.map.dispose();
            this.map = null;
        }
        this.markers.clear();
        this.polylines.clear();
        this.polygons.clear();
        this.groups.clear();
        this.openBubbles.clear();
        this.markerGroup = null;
        this.clusterProvider = null;
        this.clusterLayer = null;
        this.drawnItems = null;
        this.platform = null;
        this.ui = null;
        this.behavior = null;
        this.eventHandlers.clear();
        // Reset clustering options
        this.clusteringType = ClusteringType.COUNT;
        this.valueExtractor = null;
        this.customIconCreateFunction = null;
    }

    /**
     * Get the underlying HERE Map instance
     */
    getMap(): any {
        return this.map;
    }

    // ==================== View Methods ====================

    setCenter(lat: number, lng: number): void {
        if (this.map) {
            this.map.setCenter({lat, lng});
        }
    }

    setZoom(zoom: number): void {
        if (this.map) {
            this.map.setZoom(zoom);
        }
    }

    getCenter(): LatLng | null {
        if (!this.map) return null;
        const center = this.map.getCenter();
        return {lat: center.lat, lng: center.lng};
    }

    getZoom(): number | null {
        if (!this.map) return null;
        return this.map.getZoom();
    }

    setView(lat: number, lng: number, zoom: number): void {
        if (this.map) {
            this.map.setCenter({lat, lng});
            this.map.setZoom(zoom);
        }
    }

    fitBounds(bounds: MapBounds, padding?: number): void {
        if (!this.map) return;

        const hereBounds = new H.geo.Rect(bounds.north, bounds.west, bounds.south, bounds.east);

        this.map.getViewModel().setLookAtData(
            {
                bounds: hereBounds,
                zoom: this.map.getZoom()
            },
            true
        );
    }

    fitBoundsToMarkers(padding?: number): void {
        if (!this.map || !this.markerGroup) return;

        const bounds = this.markerGroup.getBoundingBox();
        if (bounds) {
            this.map.getViewModel().setLookAtData(
                {
                    bounds: bounds
                },
                true
            );
        }
    }

    fitBoundsToGroup(groupId: string, padding?: number): void {
        const group = this.groups.get(groupId);
        if (!group || !this.map) return;

        const bounds = group.getBoundingBox();
        if (bounds) {
            this.map.getViewModel().setLookAtData(
                {
                    bounds: bounds
                },
                true
            );
        }
    }

    // ==================== Marker Methods ====================

    addMarker(options: MarkerOptions): string {
        if (!this.map) return '';

        const id = this.generateId();
        let marker: any;
        const html = options.icon?.html || options.iconHtml;

        // Create marker with custom HTML icon if provided
        if (html) {
            const domIcon = new H.map.DomIcon(html, {
                anchor: options.icon?.iconAnchor || options.iconAnchor || {x: 0, y: 0}
            });

            marker = new H.map.DomMarker({lat: options.lat, lng: options.lng}, {icon: domIcon, data: options.data || {}});

            // Store original HTML for cluster theme recreation
            marker._originalHtml = html;
        } else if (options.icon?.iconUrl) {
            // Custom icon with URL
            let icon: any;
            if (!options.icon.iconSize && !options.icon.iconAnchor) {
                icon = new H.map.Icon(options.icon.iconUrl);
            } else {
                icon = new H.map.Icon(options.icon.iconUrl, {
                    size: options.icon.iconSize ? {w: options.icon.iconSize[0], h: options.icon.iconSize[1]} : undefined,
                    anchor: options.icon.iconAnchor ? {x: options.icon.iconAnchor[0], y: options.icon.iconAnchor[1]} : undefined
                });
            }

            marker = new H.map.Marker({lat: options.lat, lng: options.lng}, {icon: icon, data: options.data || {}});
        } else {
            // Default marker
            marker = new H.map.Marker({lat: options.lat, lng: options.lng}, {data: options.data || {}});
        }

        // Set draggable if specified
        if (options.draggable) {
            marker.draggable = true;
        }

        // Set volatility if specified
        if (options.volatility) {
            marker.setVolatility(true);
        }

        // Set z-index if specified
        if (options.zIndex !== undefined) {
            marker.setZIndex(options.zIndex);
        }

        // Add popup if provided (uniform with cluster: open on hover, close on click)
        if (options.popup || options.popupGenerator) {
            // When a popupGenerator is provided, do NOT call it at creation time.
            // It will be called on-demand when the popup actually opens via openMarkerPopup().
            // Calling it here would fire side-effects in the component (e.g. openPopupLocationKey
            // being set for every marker during loadMarkers(), corrupting popup-reopen tracking).
            const content = options.popup ? (typeof options.popup === 'string' ? options.popup : options.popup.outerHTML) : '';

            marker.addEventListener('pointerenter', (evt: any) => {
                this.openMarkerPopup(id);
                if (evt && typeof evt.stopPropagation === 'function') {
                    evt.stopPropagation();
                }
                if (!this.mouseHasMovedSinceRebuild) return;
            });
            marker.addEventListener('tap', (evt: any) => {
                this.closeAllPopups();
                if (evt && typeof evt.stopPropagation === 'function') {
                    evt.stopPropagation();
                }
            });

            // Store popup content and generator with marker
            marker.popupContent = content;
            if (options.popupGenerator) {
                marker.popupGenerator = options.popupGenerator;
            }
        }

        // Store the marker in our map first
        this.markers.set(id, marker);

        // Add to appropriate group or handle clustering
        if (options.groupId) {
            let group = this.groups.get(options.groupId);
            if (!group) {
                group = new H.map.Group();
                this.groups.set(options.groupId, group);
                this.map.addObject(group);
            }
            group.addObject(marker);
        } else if (this.clusteringEnabled) {
            // Clustering is enabled - markers should NOT go to markerGroup
            // They will be collected and added to cluster provider via rebuildClusterLayer
            // Don't add to markerGroup - just store in this.markers (done above)
        } else {
            // Add to default marker group (no clustering)
            this.markerGroup.addObject(marker);
        }

        if (options.data && options.data.type === 'movable-point') {
            marker.addEventListener('drag', (evt: any) => {
                if (!this.movePointEnabled) {
                    return;
                }
                const target = evt.target;
                const pointer = evt.currentPointer;
                if (target.data && target.data.type === 'movable-point') {
                    const pointer = evt.currentPointer;
                    const geoPoint = this.map.screenToGeo(pointer.viewportX, pointer.viewportY);
                    evt.target.setGeometry(geoPoint);
                }
                if (evt && typeof evt.stopPropagation === 'function') {
                    evt.stopPropagation();
                }
            });
        }

        return id;
    }

    removeMarker(id: string): void {
        const marker = this.markers.get(id);
        if (marker) {
            // Remove from parent (group or map)
            const parent = marker.getParentGroup();
            if (parent) {
                parent.removeObject(marker);
            }
            this.markers.delete(id);

            // Close bubble if open
            const bubble = this.openBubbles.get(id);
            if (bubble) {
                this.ui.removeBubble(bubble);
                this.openBubbles.delete(id);
            }
        }
    }

    updateMarkerPosition(id: string, lat: number, lng: number): void {
        const marker = this.markers.get(id);
        if (marker) {
            marker.setGeometry({lat, lng});
        }
    }

    getMarkerPosition(id: string): LatLng {
        const marker = this.markers.get(id);
        if (marker) {
            const position = marker.getGeometry();
            const latlng: LatLng = {lat: position.lat, lng: position.lng};
            return latlng;
        }
        return null;
    }

    updateMarkerIcon(id: string, iconHtml: string): void {
        const marker = this.markers.get(id);
        if (marker && iconHtml) {
            // Store original HTML for cluster theme recreation
            marker._originalHtml = iconHtml;

            const domIcon = new H.map.DomIcon(iconHtml, {
                anchor: {x: 0, y: 0}
            });
            marker.setIcon(domIcon);

            // If clustering is enabled, rebuild cluster layer to reflect icon changes
            if (this.clusteringEnabled) {
                this.rebuildClusterLayer();
            }
        }
    }

    updateMarkerPopup(id: string, popupContent: string): void {
        const marker = this.markers.get(id);
        if (marker && popupContent) {
            marker.popupContent = popupContent;

            // If popup is currently open, close and reopen with new content
            const bubble = this.openBubbles.get(id);
            if (bubble) {
                const wasOpen = bubble.getState() === 'open';
                bubble.close();
                if (wasOpen) {
                    // Reopen with new content
                    setTimeout(() => {
                        const newBubble = new H.ui.InfoBubble(marker.getGeometry(), {
                            content: popupContent
                        });
                        this.ui.addBubble(newBubble);
                        this.openBubbles.set(id, newBubble);
                    }, 100);
                }
            }
        }
    }

    clearMarkers(): void {
        // Clear the markers map (but don't try to remove from parent since they may be in cluster)
        this.markers.clear();

        // Clear the marker group
        if (this.markerGroup) {
            this.markerGroup.removeAll();
        }

        // For clustering: remove the cluster layer entirely and clear the provider
        // We'll recreate them when new markers are added via rebuildClusterLayer
        if (this.clusterLayer && this.map) {
            this.map.removeLayer(this.clusterLayer);
            this.clusterLayer = null;
        }
        if (this.clusterProvider) {
            this.clusterProvider = null;
        }
    }

    /**
     * Rebuild the cluster layer from all stored markers.
     * This should be called after all markers have been added when clustering is enabled.
     * This is necessary because HERE Maps clustering works best with batch data.
     */
    rebuildClusterLayer(): void {
        if (!this.map || !this.clusteringEnabled) return;

        // Remove existing cluster layer if present
        if (this.clusterLayer) {
            this.map.removeLayer(this.clusterLayer);
            this.clusterLayer = null;
            // Require the user to move their mouse before hover-triggered popups can open.
            // HERE Maps fires synthetic pointerenter events when a new cluster layer is added
            // and the cursor is already positioned over an object. This flag suppresses those
            // synthetic events while still allowing genuine hover after the next mouse move.
            this.mouseHasMovedSinceRebuild = false;

            this.clusterProvider = null;
        }

        // CRITICAL: Ensure markerGroup is empty when clustering is active
        // Markers should ONLY be displayed via the cluster layer, not markerGroup
        if (this.markerGroup) {
            this.markerGroup.removeAll();
        }

        // Collect data points from all stored markers
        const dataPoints: any[] = [];
        this.markers.forEach((marker, id) => {
            const pos = marker.getGeometry();
            const originalData = marker.getData() || {};

            // Create a new object to avoid modifying frozen/sealed objects
            // Spread the original data and add our marker metadata
            const clusterData = {
                ...originalData,
                _markerId: id,
                _markerHtml: marker._originalHtml,
                _popupContent: marker.popupContent,
                _popupGenerator: marker.popupGenerator // Preserve popup generator
            };

            // Calculate weight based on clustering type
            let weight = 1;
            if (this.clusteringType === ClusteringType.SUM && this.valueExtractor) {
                weight = this.valueExtractor(originalData) || 1;
            }

            dataPoints.push(new H.clustering.DataPoint(pos.lat, pos.lng, weight, clusterData));
        });

        // Only create cluster layer if we have data points
        if (dataPoints.length === 0) return;

        // Create the clustering provider with the stored options
        const options = this.storedClusterOptions;
        this.clusterProvider = new H.clustering.Provider(dataPoints, {
            clusteringOptions: {
                eps: options?.maxRadius ?? 32,
                minWeight: 2
            },
            theme: this.createClusterTheme(options)
        });

        // Create and add the cluster layer
        this.clusterLayer = new H.map.layer.ObjectLayer(this.clusterProvider);
        this.map.addLayer(this.clusterLayer);
    }

    getMarkerData(id: string): any {
        const marker = this.markers.get(id);
        return marker ? marker.getData() : null;
    }

    openMarkerPopup(id: string): void {
        const marker = this.markers.get(id);

        if (!marker) {
            return;
        }
        if (!this.ui) {
            return;
        }

        // Always close and recreate bubble to ensure fresh content
        const existingBubble = this.openBubbles.get(id);
        if (existingBubble) {
            this.ui.removeBubble(existingBubble);
            this.openBubbles.delete(id);
        }

        // Close all other open bubbles first (autoClose behavior like Leaflet)
        this.closeAllPopups();

        // If marker has a popup generator, regenerate content with current data
        let content = marker.popupContent;
        if (marker.popupGenerator) {
            const data = marker.getData();
            content = marker.popupGenerator(data);
            // Update stored content as well
            marker.popupContent = content;
        }

        if (!content) {
            return;
        }

        // Create new bubble with current popup content
        const bubble = new H.ui.InfoBubble(marker.getGeometry(), {
            content: content
        });

        this.ui.addBubble(bubble);
        this.openBubbles.set(id, bubble);
    }

    closeMarkerPopup(id: string): void {
        const bubble = this.openBubbles.get(id);
        if (bubble) {
            bubble.close();
        }
    }

    // ==================== Clustering Methods ====================

    enableClustering(options?: ClusterOptions): void {
        if (!this.map) return;

        // Store clustering options for later use
        this.clusteringEnabled = true;
        this.storedClusterOptions = options || null;
        this.clusteringType = options?.clusteringType ?? ClusteringType.COUNT;
        this.valueExtractor = options?.valueExtractor ?? null;
        this.customIconCreateFunction = options?.iconCreateFunction ?? null;

        // If cluster layer already exists, remove it first
        if (this.clusterLayer && this.map) {
            this.map.removeLayer(this.clusterLayer);
            this.clusterLayer = null;
            this.clusterProvider = null;
        }

        // If there are existing markers, move them from markerGroup to cluster
        // Otherwise, just wait for markers to be added and rebuildClusterLayer to be called
        if (this.markers.size > 0) {
            // Move existing markers out of markerGroup
            this.markers.forEach((marker) => {
                const parent = marker.getParentGroup();
                if (parent) {
                    parent.removeObject(marker);
                }
            });

            // Build the cluster layer with existing markers
            this.rebuildClusterLayer();
        }
        // If no markers exist yet, the cluster layer will be created when rebuildClusterLayer is called
    }

    /**
     * Creates a custom theme for HERE Maps clustering
     */
    private createClusterTheme(options?: ClusterOptions): any {
        const self = this;

        return {
            getClusterPresentation: (cluster: any) => {
                // Get all noise points (individual data points) in this cluster
                const noisePoints: any[] = [];
                cluster.forEachDataPoint((dataPoint: any) => {
                    noisePoints.push(dataPoint);
                });

                let displayValue: number;
                let clusterData: any = {noisePoints, isCluster: true};

                const clusterPosition = cluster.getPosition();
                const clusterLatLng = {lat: clusterPosition.lat, lng: clusterPosition.lng};
                const clusterProxy = {
                    getChildCount: () => noisePoints.length,
                    getAllChildMarkers: () =>
                        noisePoints.map((point) => ({
                            options: {data: point.getData()},
                            data: point.getData()
                        }))
                };
                if (self.clusteringType === ClusteringType.SUM) {
                    // SUM clustering: sum the weights (which contain extracted values)
                    displayValue = 0;
                    noisePoints.forEach((point) => {
                        displayValue += point.getWeight() || 0;
                    });
                    clusterData.totalValue = displayValue;
                } else {
                    // COUNT clustering: count the noise points
                    displayValue = noisePoints.length;
                    clusterData.count = displayValue;
                }

                // Collect all popup content from child markers for the cluster popup
                const allPopupContents: string[] = [];
                noisePoints.forEach((point) => {
                    const pointData = point.getData();
                    if (pointData?._popupContent) {
                        allPopupContents.push(pointData._popupContent);
                    }
                });

                // If a custom icon function is provided, use it
                if (self.customIconCreateFunction) {
                    const icon = self.customIconCreateFunction(clusterProxy);
                    // The custom function should return a Leaflet DivIcon-like object with html property
                    if (icon && icon.options && icon.options.html) {
                        const domIcon = new H.map.DomIcon(icon.options.html, {
                            anchor: icon.options.iconAnchor || {x: 20, y: 20}
                        });
                        const clusterMarker = new H.map.DomMarker(cluster.getPosition(), {
                            icon: domIcon,
                            data: clusterData,
                            min: cluster.getMinZoom(),
                            max: cluster.getMaxZoom()
                        });

                        // Uniform with location: open on hover; close only on click or when another opens
                        clusterMarker.addEventListener('pointerenter', (evt: any) => {
                            if (options?.onClusterMouseOver) {
                                options.onClusterMouseOver(clusterProxy, clusterLatLng);
                            } else {
                                self.showClusterPopup(clusterPosition, noisePoints, clusterProxy);
                            }
                            if (evt && typeof evt.stopPropagation === 'function') {
                                evt.stopPropagation();
                            }
                        });
                        clusterMarker.addEventListener('pointerleave', () => {
                            if (options?.onClusterMouseOut) {
                                options.onClusterMouseOut();
                            }
                        });
                        clusterMarker.addEventListener('tap', (evt: any) => {
                            self.closeAllPopups();
                            if (options?.onClusterClick) {
                                options.onClusterClick(clusterProxy);
                            }
                            if (evt && typeof evt.stopPropagation === 'function') {
                                evt.stopPropagation();
                            }
                        });

                        return clusterMarker;
                    }
                }

                // Default cluster visualization
                const html = self.createDefaultClusterHtml(displayValue);
                const domIcon = new H.map.DomIcon(html, {
                    anchor: {x: 20, y: 20}
                });

                const clusterMarker = new H.map.DomMarker(cluster.getPosition(), {
                    icon: domIcon,
                    data: clusterData,
                    min: cluster.getMinZoom(),
                    max: cluster.getMaxZoom()
                });

                clusterMarker.addEventListener('tap', (evt: any) => {
                    self.closeAllPopups(); // Uniform: click closes popover
                    if (options?.zoomToBoundsOnClick !== false) {
                        const pos = cluster.getPosition();
                        const targetZoom = cluster.getMaxZoom() + 1;

                        self.map.getViewModel().setLookAtData(
                            {
                                position: {lat: pos.lat, lng: pos.lng},
                                zoom: Math.min(targetZoom)
                            },
                            false // No animation - instant zoom
                        );
                    }
                    if (evt && typeof evt.stopPropagation === 'function') {
                        evt.stopPropagation();
                    }
                });

                return clusterMarker;
            },

            getNoisePresentation: (noisePoint: any) => {
                // Render individual markers (not clustered)
                const data = noisePoint.getData();
                const markerId = data?._markerId;
                const pos = noisePoint.getPosition();

                // CRITICAL: Get the minimum zoom level at which this noise point should be visible
                // Without this, the noise point will show at ALL zoom levels, even when it should be clustered
                const minZoom = noisePoint.getMinZoom();

                // For SUM clustering with custom icon function, use the custom icon
                // This ensures single-item locations also show as numbered bubbles
                if (self.clusteringType === ClusteringType.SUM && self.customIconCreateFunction) {
                    // Create a single-item cluster proxy for the custom icon function
                    const singleItemProxy = {
                        getChildCount: () => 1,
                        getAllChildMarkers: () => [
                            {
                                options: {data: data},
                                data: data
                            }
                        ]
                    };

                    const icon = self.customIconCreateFunction(singleItemProxy);
                    if (icon && icon.options && icon.options.html) {
                        const domIcon = new H.map.DomIcon(icon.options.html, {
                            anchor: icon.options.iconAnchor || {x: 20, y: 20}
                        });
                        const marker = new H.map.DomMarker(pos, {
                            icon: domIcon,
                            data: data,
                            min: minZoom // Only show this marker at zoom levels >= minZoom
                        });

                        // Uniform: open on hover; close only on click or when another opens
                        if (data._popupContent || data._popupGenerator) {
                            marker.popupContent = data._popupContent;
                            marker.popupGenerator = data._popupGenerator;

                            const openPopup = () => {
                                if (data._markerId && self.markers.has(data._markerId)) {
                                    self.openMarkerPopup(data._markerId);
                                } else {
                                    self.showClusterMarkerPopup(marker);
                                    if (!self.mouseHasMovedSinceRebuild) return;
                                }
                            };
                            marker.addEventListener('pointerenter', (evt: any) => {
                                openPopup();
                                if (evt && typeof evt.stopPropagation === 'function') {
                                    evt.stopPropagation();
                                }
                            });
                            marker.addEventListener('tap', (evt: any) => {
                                self.closeAllPopups();
                                if (evt && typeof evt.stopPropagation === 'function') {
                                    evt.stopPropagation();
                                }
                            });
                        }

                        return marker;
                    }
                }

                // For COUNT clustering or fallback: use the original marker appearance
                const originalMarker = self.markers.get(markerId);

                if (originalMarker && data._markerHtml) {
                    const domIcon = new H.map.DomIcon(data._markerHtml, {
                        anchor: {x: 0, y: 0}
                    });
                    const marker = new H.map.DomMarker(pos, {
                        icon: domIcon,
                        data: data,
                        min: minZoom // Only show this marker at zoom levels >= minZoom
                    });

                    // Uniform: open on hover; close only on click or when another opens
                    if (data._popupContent || data._popupGenerator) {
                        marker.popupContent = data._popupContent;
                        marker.popupGenerator = data._popupGenerator;

                        const openPopup = () => {
                            if (data._markerId && self.markers.has(data._markerId)) {
                                self.openMarkerPopup(data._markerId);
                            } else {
                                self.showClusterMarkerPopup(marker);
                                if (!self.mouseHasMovedSinceRebuild) return;
                            }
                        };
                        marker.addEventListener('pointerenter', (evt: any) => {
                            openPopup();
                            if (evt && typeof evt.stopPropagation === 'function') {
                                evt.stopPropagation();
                            }
                        });
                        marker.addEventListener('tap', (evt: any) => {
                            self.closeAllPopups();
                            if (evt && typeof evt.stopPropagation === 'function') {
                                evt.stopPropagation();
                            }
                        });
                    }

                    return marker;
                }

                // Fallback: create a simple marker
                const marker = new H.map.Marker(pos, {
                    data: data,
                    min: minZoom // Only show this marker at zoom levels >= minZoom
                });

                if (data._popupContent || data._popupGenerator) {
                    marker.popupContent = data._popupContent;
                    marker.popupGenerator = data._popupGenerator;

                    const openPopup = () => {
                        if (data._markerId && self.markers.has(data._markerId)) {
                            self.openMarkerPopup(data._markerId);
                        } else {
                            self.showClusterMarkerPopup(marker);
                            if (!self.mouseHasMovedSinceRebuild) return;
                        }
                    };
                    marker.addEventListener('pointerenter', (evt: any) => {
                        openPopup();
                        if (evt && typeof evt.stopPropagation === 'function') {
                            evt.stopPropagation();
                        }
                    });
                    marker.addEventListener('tap', (evt: any) => {
                        self.closeAllPopups();
                        if (evt && typeof evt.stopPropagation === 'function') {
                            evt.stopPropagation();
                        }
                    });
                }

                return marker;
            }
        };
    }

    /**
     * Creates HTML for default cluster markers
     */
    private createDefaultClusterHtml(displayValue: number): string {
        let size = 'small';
        let dimension = 30;
        if (displayValue > 100) {
            size = 'large';
            dimension = 50;
        } else if (displayValue > 10) {
            size = 'medium';
            dimension = 40;
        }

        return `<div class="marker-cluster marker-cluster-${size}" style="
            width: ${dimension}px;
            height: ${dimension}px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: ${size === 'large' ? 'rgba(244, 67, 54, 0.8)' : size === 'medium' ? 'rgba(255, 152, 0, 0.8)' : 'rgba(76, 175, 80, 0.8)'};
            color: white;
            font-weight: bold;
            font-size: ${size === 'large' ? '16px' : size === 'medium' ? '14px' : '12px'};
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            cursor: pointer;
        "><span>${displayValue}</span></div>`;
    }

    /**
     * Shows popup for a marker within a cluster
     */
    private showClusterMarkerPopup(marker: any): void {
        if (!this.ui) return;
        if (Date.now() < this.hoverPopupCooldownUntil) return;

        // Close any existing bubbles
        this.closeAllPopups();

        // If marker has a popup generator, regenerate content with current data
        let content = marker.popupContent;
        if (marker.popupGenerator) {
            const data = marker.getData ? marker.getData() : marker.data;
            content = marker.popupGenerator(data);
            marker.popupContent = content;
        }

        if (!content) {
            return;
        }

        const bubble = new H.ui.InfoBubble(marker.getGeometry(), {
            content: content
        });

        this.ui.addBubble(bubble);
        // Store with a special key for cluster markers
        this.openBubbles.set('cluster-marker-popup', bubble);
    }

    /**
     * Shows popup for a SUM cluster (map-based planning) with all orders from all location points
     * Collects all haulier orders and displays them in a single table with rows
     */
    private showClusterPopup(position: any, noisePoints: any[], clusterProxy: any): void {
        if (!this.ui) return;

        this.closeAllPopups();

        let content: string | null = null;
        if (Date.now() < this.hoverPopupCooldownUntil) return;

        if (noisePoints.length > 0) {
            const firstPoint = noisePoints[0];
            const pointData = firstPoint.getData();

            if (pointData?._popupGenerator && typeof pointData._popupGenerator === 'function') {
                const allHaulierOrders: any[] = [];
                noisePoints.forEach((point) => {
                    const data = point.getData();
                    if (data?.haulierOrders && Array.isArray(data.haulierOrders)) {
                        allHaulierOrders.push(...data.haulierOrders);
                    }
                });

                if (allHaulierOrders.length > 0) {
                    const combinedData = {
                        ...pointData,
                        haulierOrders: allHaulierOrders
                    };

                    content = pointData._popupGenerator(combinedData);
                }
            }
        }

        if (!content) {
            const allHaulierOrders: any[] = [];
            noisePoints.forEach((point) => {
                const pointData = point.getData();
                if (pointData?.haulierOrders && Array.isArray(pointData.haulierOrders)) {
                    allHaulierOrders.push(...pointData.haulierOrders);
                }
            });

            if (allHaulierOrders.length > 0) {
                content = this.generateCombinedClusterPopup(allHaulierOrders);
            } else {
                content = `<div style="padding: 10px;"><strong>${noisePoints.length} items</strong></div>`;
            }
        }

        const bubble = new H.ui.InfoBubble(position, {content});
        this.ui.addBubble(bubble);
        this.openBubbles.set('cluster-popup', bubble);
    }

    /**
     * Generates a combined popup HTML for all haulier orders in a cluster
     */
    private generateCombinedClusterPopup(haulierOrders: any[]): string {
        let content = '';

        const header =
            '<div style="width:600px; max-height:250px; overflow-y:auto; overflow-x:hidden; font-size:11px;">' +
            '<table style="table-layout:fixed; width:100%; text-align:center;">' +
            '<colgroup>' +
            '<col style="width:60px;">' +
            '<col style="width:60px;">' +
            '<col style="width:70px;">' +
            '<col style="width:60px;">' +
            '<col style="width:20px;">' +
            '<col style="width:70px;">' +
            '<col style="width:60px;">' +
            '<col style="width:50px;">' +
            '</colgroup>' +
            '<tr>' +
            '<th></th>' +
            '<th>ID|ODR No|LD No</th>' +
            '<th>Collect From</th>' +
            '<th>Collect When</th>' +
            '<th>PL</th>' +
            '<th>Deliver To</th>' +
            '<th>Deliver When</th>' +
            '<th>Goods Type</th>' +
            '</tr>';
        const footer = '</table></div>';

        for (const haulierOrder of haulierOrders) {
            let goodsType = haulierOrder.customGoodsType?.name ? haulierOrder.customGoodsType?.name : '';
            const goodsTypeDetails: string[] = [];
            if (haulierOrder.customGoodsType?.customGoodsTypeProperties?.length > 0) {
                haulierOrder.customGoodsType.customGoodsTypeProperties.forEach((prop: any) => {
                    goodsTypeDetails.push(prop.name);
                });
            }
            if (goodsTypeDetails.length > 0) {
                goodsType += ' (' + goodsTypeDetails.join(', ') + ')';
            }

            const addButtonId = 'add-' + haulierOrder.id;
            const removeButtonId = 'remove-' + haulierOrder.id;
            const button =
                `<td><button style="display:block" class="addToRunButton" id="${addButtonId}" onclick="event.stopPropagation(); document.querySelector('.data-holder').setAttribute('data-haulierorder-id', '${haulierOrder.id}'); document.querySelector('.addToRunButtonProxy').click(); document.getElementById('${addButtonId}').style.display = 'none'; document.getElementById('${removeButtonId}').style.display = 'block'">Add</button>` +
                `<button style="display:none" class="removeToRunButton" id="${removeButtonId}" onclick="event.stopPropagation(); document.querySelector('.data-holder').setAttribute('data-haulierorder-id', '${haulierOrder.id}'); document.querySelector('.removeToRunButtonProxy').click(); document.getElementById('${addButtonId}').style.display = 'block'; document.getElementById('${removeButtonId}').style.display = 'none';">Remove</button></td>`;

            content +=
                '<tr>' +
                button +
                `<td style="word-break:break-all; white-space:normal;"><a href='/orders/haulier-orders/${haulierOrder.id}/edit' target='_blank'>${haulierOrder.id} | ${haulierOrder?.orderNo || ''} | ${haulierOrder?.loadNo || ''}</a></td>` +
                `<td style="word-break:break-word; white-space:normal;">${haulierOrder.customer || ''}${haulierOrder.collectionPoint || ''} - ${haulierOrder.collectionPointTown || ''}</td>` +
                `<td>${haulierOrder.collectionPointStartDate || ''} ${haulierOrder.collectionPointStartTime || ''}</td>` +
                `<td>${haulierOrder.pallets || 0}</td>` +
                `<td style="word-break:break-word; white-space:normal;">${haulierOrder.deliveryPoint || ''} - ${haulierOrder.deliveryPointTown || ''}</td>` +
                `<td>${haulierOrder.deliveryPointStartDate || ''} ${haulierOrder.deliveryPointStartTime || ''}</td>` +
                `<td>${goodsType}</td>` +
                '</tr>';
        }

        return header + content + footer;
    }

    /**
     * Shows popup for a COUNT cluster (fleet view) with all items
     */
    private showCountClusterPopup(position: any, noisePoints: any[]): void {
        if (!this.ui) return;

        // Close any existing bubbles
        this.closeAllPopups();

        // Combine all popup contents
        const contents: string[] = [];
        noisePoints.forEach((point) => {
            const pointData = point.getData();
            if (pointData?._popupContent) {
                contents.push(pointData._popupContent);
            }
        });

        const content =
            contents.length > 0
                ? `<div style="max-height: 400px; overflow-y: auto;">${contents.join('<hr style="margin: 10px 0; border: none; border-top: 1px solid #ddd;">')}</div>`
                : `<div style="padding: 10px;"><strong>${noisePoints.length} items</strong></div>`;

        const bubble = new H.ui.InfoBubble(position, {content});
        this.ui.addBubble(bubble);
        this.openBubbles.set('cluster-popup', bubble);
    }

    disableClustering(): void {
        if (!this.map) return;

        // Remove cluster layer
        if (this.clusterLayer) {
            this.map.removeLayer(this.clusterLayer);
            this.clusterLayer = null;
        }

        // Clear cluster provider
        this.clusterProvider = null;

        // Move markers back to marker group
        this.markers.forEach((marker) => {
            if (this.markerGroup) {
                this.markerGroup.addObject(marker);
            }
        });

        // Reset clustering options
        this.clusteringType = ClusteringType.COUNT;
        this.valueExtractor = null;
        this.customIconCreateFunction = null;
    }

    /**
     * Get the current clustering type
     */
    getClusteringType(): ClusteringType {
        return this.clusteringType;
    }

    /**
     * Get the value extractor function for SUM clustering
     */
    getValueExtractor(): ((markerData: any) => number) | null {
        return this.valueExtractor;
    }

    // ==================== Polyline Methods ====================

    addPolyline(options: PolylineOptions): string {
        if (!this.map) return '';

        const id = this.generateId();

        // Convert coordinates to HERE Maps format
        const lineString = new H.geo.LineString();
        options.coordinates.forEach((coord) => {
            lineString.pushPoint({lat: coord.lat, lng: coord.lng});
        });

        // Build style object - only include lineDash if dashArray is provided
        const style: any = {
            lineWidth: options.weight || 5,
            strokeColor: options.color || '#1E88E5',
            fillColor: 'transparent'
        };
        if (options.dashArray) {
            style.lineDash = this.convertDashArray(options.dashArray);
        }

        // Create polyline
        const polyline = new H.map.Polyline(lineString, {
            style,
            data: {opacity: options.opacity || 1}
        });

        this.map.addObject(polyline);
        this.polylines.set(id, polyline);
        return id;
    }

    addPolylineFromEncoded(encoded: string, options?: PolylineStyleOptions): string {
        const coordinates = decodePolyline(encoded);
        return this.addPolyline({
            coordinates,
            color: options?.color,
            weight: options?.weight,
            opacity: options?.opacity,
            dashArray: options?.dashArray
        });
    }

    addMultiPolylineFromEncoded(encodedSegments: string, options?: PolylineStyleOptions): string {
        if (!this.map) return '';

        const id = this.generateId();
        const segments = encodedSegments.split(',');
        const polylines: any[] = [];

        segments.forEach((segment) => {
            const lineString = new H.geo.LineString.fromFlexiblePolyline(segment.trim());

            // Build style object - only include lineDash if dashArray is provided
            const style: any = {
                lineWidth: options?.weight || 5,
                strokeColor: options?.color || '#1E88E5'
            };
            if (options?.dashArray) {
                style.lineDash = this.convertDashArray(options.dashArray);
            }

            const polyline = new H.map.Polyline(lineString, {
                style,
                data: {opacity: options?.opacity || 1}
            });

            polylines.push(polyline);
            this.map.addObject(polyline);
        });

        this.polylines.set(id, polylines);
        return id;
    }

    removePolyline(id: string): void {
        const polyline = this.polylines.get(id);
        if (polyline) {
            if (Array.isArray(polyline)) {
                polyline.forEach((p) => this.map.removeObject(p));
            } else {
                this.map.removeObject(polyline);
            }
            this.polylines.delete(id);
        }
    }

    clearPolylines(): void {
        this.polylines.forEach((polyline, id) => {
            this.removePolyline(id);
        });
        this.polylines.clear();
    }

    fitBoundsToPolyline(id: string, padding?: number): void {
        const polyline = this.polylines.get(id);
        if (!polyline || !this.map) return;

        const polylines = Array.isArray(polyline) ? polyline : [polyline];

        if (polylines.length > 0) {
            const bounds = polylines[0].getBoundingBox();

            // Extend bounds for all polylines
            for (let i = 1; i < polylines.length; i++) {
                const b = polylines[i].getBoundingBox();
                if (b) {
                    bounds.mergeRect(b);
                }
            }

            if (bounds) {
                this.map.getViewModel().setLookAtData({bounds}, true);
            }
        }
    }

    // ==================== Polygon Methods ====================

    addPolygon(coordinates: LatLng[], options?: PolygonOptions): string {
        if (!this.map) return '';

        const id = this.generateId();

        // Convert coordinates to HERE Maps format
        const lineString = new H.geo.LineString();
        coordinates.forEach((coord) => {
            lineString.pushPoint({lat: coord.lat, lng: coord.lng});
        });

        // Create polygon
        const polygon = new H.map.Polygon(lineString, {
            style: {
                fillColor: options?.fillColor || 'rgba(30, 136, 229, 0.3)',
                strokeColor: options?.strokeColor || '#1E88E5',
                lineWidth: options?.strokeWeight || 2
            }
        });

        this.map.addObject(polygon);
        this.polygons.set(id, polygon);
        return id;
    }

    removePolygon(id: string): void {
        const polygon = this.polygons.get(id);
        if (polygon) {
            this.map.removeObject(polygon);
            this.polygons.delete(id);
        }
    }

    clearPolygons(): void {
        this.polygons.forEach((polygon, id) => {
            this.removePolygon(id);
        });
        this.polygons.clear();
    }

    getPolygonCoordinates(id: string): LatLng[] | null {
        const polygon = this.polygons.get(id);
        if (!polygon) return null;

        const geometry = polygon.getGeometry();
        const points = geometry.getExterior().getPointCount();
        const coordinates: LatLng[] = [];

        for (let i = 0; i < points - 1; i++) {
            // -1 to skip the closing point
            const point = geometry.getExterior().extractPoint(i);
            coordinates.push({lat: point.lat, lng: point.lng});
        }

        return coordinates;
    }

    fitBoundsToPolygon(id: string, padding?: number): void {
        const polygon = this.polygons.get(id);
        if (!polygon || !this.map) return;

        const bounds = polygon.getBoundingBox();
        if (bounds) {
            this.map.getViewModel().setLookAtData({bounds}, true);
        }
    }

    enablePointMovement(pointId: string) {
        this.movePointEnabled = true;
        this.movePointId = pointId;
    }

    enablePolygonDrawing(
        polygonId: string,
        onComplete: (coordinates: LatLng[], polygonId?: string) => void,
        onEdit?: (coordinates: LatLng[]) => void
    ): void {
        this.onPolygonComplete = onComplete;
        this.onPolygonEdit = onEdit;
        this.polygonDrawingEnabled = true;
        if (!this.groups.has('vertexMarkerGroup')) {
            this.createGroup('vertexMarkerGroup');
            const vertexMarkerGroup = this.groups.get('vertexMarkerGroup');
            vertexMarkerGroup.addEventListener(
                'drag',
                (evt: any) => {
                    if (!this.polygonDrawingEnabled) {
                        return;
                    }

                    // move the marker
                    const pointer = evt.currentPointer;
                    const geoPoint = this.map.screenToGeo(pointer.viewportX, pointer.viewportY);
                    evt.target.setGeometry(geoPoint);

                    // move the polygon's vertex
                    const polygon = this.polygons.get(this.polygonDrawingId);
                    const geoLineString = polygon.getGeometry().getExterior();
                    const data = evt.target.getData();
                    if (data && data.type === 'vertex-marker') {
                        geoLineString.removePoint(data.index);
                        geoLineString.insertPoint(data.index, geoPoint);
                        polygon.setGeometry(new H.geo.Polygon(geoLineString));
                    }

                    if (evt && typeof evt.stopPropagation === 'function') {
                        evt.stopPropagation();
                    }
                },
                true
            );
        }
        if (polygonId) {
            const polygon = this.polygons.get(polygonId);
            if (polygon) {
                this.polygonFirstLat = this.polygonFirstLng = undefined;
                this.polygonDrawingId = polygonId;
                polygon
                    .getGeometry()
                    .getExterior()
                    .eachLatLngAlt((lat, lng, alt, index) => {
                        if (lat != this.polygonFirstLat || lng != this.polygonFirstLng) {
                            this.addPolygonVertexMarker({lat, lng}, index);
                            console.log('index: ' + index + ', lat: ' + lat + ', lng: ' + lng);
                            if (!this.polygonFirstLat && !this.polygonFirstLng) {
                                this.polygonFirstLat = lat;
                                this.polygonFirstLng = lng;
                            }
                        }
                    });
            }
        }
    }

    completePolygonDrawing(): void {
        if (this.polygonDrawingId) {
            const polygon = this.polygons.get(this.polygonDrawingId);
            let geoCoordinates: LatLng[] = [];
            let coordinates = polygon.getGeometry().getExterior().getLatLngAltArray();
            for (let i = 0; i < coordinates.length; i += 3) {
                let latlng: LatLng = {lat: coordinates[i], lng: coordinates[i + 1]};
                geoCoordinates.push(latlng);
            }
            this.onPolygonComplete(geoCoordinates, this.polygonDrawingId);
            this.polygonDrawingId = null;
        }
    }

    disablePolygonDrawing(): void {
        if (this.polygonDrawingId) {
            this.removePolygon(this.polygonDrawingId);
            this.polygonDrawingId = null;
        }
        this.polygonDrawingEnabled = false;
        this.movePointEnabled = false;
        this.clearPolygonVertexMarkers();
    }

    handleTapEvent(evt: any): void {
        if (!this.polygonDrawingEnabled) {
            return;
        }

        const pointer = evt.currentPointer;
        const geoPoint = this.map.screenToGeo(pointer.viewportX, pointer.viewportY);

        if (!this.polygonDrawingId) {
            this.addPolygonVertexMarker(geoPoint, 0);
            let points: LatLng[] = [geoPoint];
            this.polygonDrawingId = this.addPolygon(points);
        } else {
            const polygon = this.polygons.get(this.polygonDrawingId);
            if (polygon) {
                const polygonExterior = polygon.getGeometry().getExterior();
                const pointCount = polygonExterior.getPointCount();

                this.addPolygonVertexMarker(geoPoint, pointCount);
                polygonExterior.insertPoint(pointCount, geoPoint);
                polygon.setGeometry(new H.geo.Polygon(polygonExterior));

                console.log(`Adding vertex: ${pointCount}`);
            }
            console.log('Polygon vertices: ' + (polygon == null ? 0 : polygon.getGeometry().getExterior().toString()));
        }

        // stop propagating the drag event, so the map doesn't move
        evt.stopPropagation();
    }

    // ==================== Popup Methods ====================

    showPopup(lat: number, lng: number, content: string | HTMLElement): void {
        if (!this.ui) return;

        this.closeAllPopups();

        const htmlContent = typeof content === 'string' ? content : content.outerHTML;
        const bubble = new H.ui.InfoBubble({lat, lng}, {content: htmlContent});
        this.ui.addBubble(bubble);
        this.openBubbles.set('show-popup', bubble);
        this.hoverPopupCooldownUntil = Date.now() + 500;
    }

    closeAllPopups(): void {
        this.openBubbles.forEach((bubble, key) => {
            try {
                bubble.close();
                if (this.ui) {
                    this.ui.removeBubble(bubble);
                }
            } catch (e) {
                // Silently handle errors when closing bubbles
            }
        });
        this.openBubbles.clear();
    }

    isAnyPopupOpen(): boolean {
        return this.openBubbles.size > 0;
    }

    // ==================== Group Methods ====================

    createGroup(id: string): void {
        if (!this.groups.has(id)) {
            const group = new H.map.Group();
            this.groups.set(id, group);
            this.map.addObject(group);
        }
    }

    addToGroup(groupId: string, objectId: string): void {
        const group = this.groups.get(groupId);
        if (!group) return;

        // Find object in markers, polylines, or polygons
        const marker = this.markers.get(objectId);
        const polyline = this.polylines.get(objectId);
        const polygon = this.polygons.get(objectId);

        const obj = marker || polyline || polygon;
        if (obj) {
            // Remove from current parent
            const parent = obj.getParentGroup();
            if (parent) {
                parent.removeObject(obj);
            }

            // Add to new group
            if (Array.isArray(obj)) {
                obj.forEach((o) => group.addObject(o));
            } else {
                group.addObject(obj);
            }
        }
    }

    clearGroup(groupId: string): void {
        const group = this.groups.get(groupId);
        if (group) {
            group.removeAll();
        }
    }

    removeGroup(groupId: string): void {
        const group = this.groups.get(groupId);
        if (group) {
            this.map.removeObject(group);
            this.groups.delete(groupId);
        }
    }

    getGroupBounds(groupId: string): MapBounds | null {
        const group = this.groups.get(groupId);
        if (!group) return null;

        const bounds = group.getBoundingBox();
        if (!bounds) return null;

        return {
            north: bounds.getTop(),
            south: bounds.getBottom(),
            east: bounds.getRight(),
            west: bounds.getLeft()
        };
    }

    // ==================== Utility Methods ====================

    invalidateSize(): void {
        if (this.map) {
            const viewport = this.map.getViewPort();
            viewport.resize();
        }
    }

    panTo(lat: number, lng: number): void {
        if (this.map) {
            this.map.setCenter({lat, lng}, true); // true for animation
        }
    }

    clearAll(): void {
        this.clearMarkers();
        this.clearPolylines();
        this.clearPolygons();
        this.closeAllPopups();
    }

    on(event: MapEventType, handler: (e: MapEvent) => void): void {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event)!.push(handler);

        // Subscribe to HERE Maps events
        if (this.map) {
            const hereEventType = this.mapEventTypeToHere(event);
            if (hereEventType) {
                this.map.addEventListener(hereEventType, (evt: any) => {
                    const mapEvent: MapEvent = {
                        type: event,
                        latlng: evt.currentPointer
                            ? {
                                  lat: this.map.screenToGeo(evt.currentPointer.viewportX, evt.currentPointer.viewportY).lat,
                                  lng: this.map.screenToGeo(evt.currentPointer.viewportX, evt.currentPointer.viewportY).lng
                              }
                            : undefined,
                        target: evt.target,
                        originalEvent: evt.originalEvent
                    };
                    handler(mapEvent);
                });
            }
        }
    }

    off(event: MapEventType, handler?: (e: MapEvent) => void): void {
        if (!handler) {
            // Remove all handlers for this event
            this.eventHandlers.delete(event);
        } else {
            const handlers = this.eventHandlers.get(event);
            if (handlers) {
                const index = handlers.indexOf(handler);
                if (index > -1) {
                    handlers.splice(index, 1);
                }
            }
        }
    }

    // ==================== Helper Methods ====================

    private convertDashArray(dashArray: string): number[] {
        return dashArray.split(/[\s,]+/).map((v) => parseInt(v, 10));
    }

    private mapEventTypeToHere(eventType: MapEventType): string | null {
        const eventMap: {[key: string]: string} = {
            click: 'tap',
            dblclick: 'dbltap',
            drag: 'drag',
            dragstart: 'dragstart',
            dragend: 'dragend',
            moveend: 'mapviewchangeend',
            zoomend: 'mapviewchangeend'
        };
        return eventMap[eventType] || null;
    }

    private addPolygonVertexMarker(geoPoint: LatLng, index: number): void {
        const iconHtml =
            '<svg width="8" height="8" xmlns="http://www.w3.org/2000/svg">' +
            '<circle cx="4" cy="4" r="4" fill="transparent" stroke="red" stroke-width="4"/>' +
            '</svg>';

        this.addMarker({
            lat: geoPoint.lat,
            lng: geoPoint.lng,
            icon: {
                html: iconHtml,
                //iconAnchor: [4,4],  // anchor is not supported by DomIcon
                className: 'vertex-marker'
            },
            draggable: true,
            groupId: 'vertexMarkerGroup',
            data: {type: 'vertex-marker', index: index}
        });
    }

    private clearPolygonVertexMarkers() {
        let group = this.groups.get('vertexMarkerGroup');
        if (group) {
            group.removeAll();
        }
    }
}
